<template>
  <template v-if="inline">
    <!-- Inline: [logo VeCy] 5/5⭐ -->
    <span
      ref="triggerRef"
      class="relative inline-flex items-center gap-1 shrink-0 cursor-help"
      :class="size === 'md' ? 'text-sm' : size === 'sm' ? 'text-sm' : 'text-xs'"
      :aria-label="tooltipText"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      @click.stop="onClick"
    >
      <VecyLogo :size="size === 'md' ? 'md' : size === 'sm' ? 'sm' : 'xs'" />
      <span class="font-semibold text-gray-800 tabular-nums">{{ scoreDisplay }}</span>
      <span class="leading-none" aria-hidden="true">⭐</span>
    </span>

    <Teleport to="body">
      <div
        v-if="isTooltipVisible"
        class="fixed z-[9999] px-2.5 py-1.5 text-[11px] leading-snug text-white bg-gray-900 rounded-lg shadow-xl whitespace-nowrap pointer-events-none"
        :style="tooltipStyle"
        role="tooltip"
      >
        {{ tooltipText }}
        <span
          class="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-gray-900 rotate-45"
          aria-hidden="true"
        />
      </div>
    </Teleport>
  </template>

  <!-- Compact: píldoras del resumen por zona -->
  <div
    v-else-if="compact"
    class="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 min-w-0 leading-snug"
    :class="size === 'sm' ? 'text-[11px]' : 'text-xs'"
    :title="ariaText"
  >
    <VecyLogo :size="size === 'sm' ? 'xs' : 'sm'" />
    <span class="font-semibold text-gray-900 tabular-nums">{{ scoreDisplay }}</span>
    <span v-if="showReviewCount && reviewCount > 0" class="text-gray-500 font-normal">
      ({{ reviewCount }})
    </span>
    <span class="leading-none" aria-hidden="true">⭐</span>
  </div>

  <!-- Bloque (legacy / otros usos) -->
  <div
    v-else
    class="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 min-w-0 leading-snug"
    :class="size === 'sm' ? 'text-[11px]' : 'text-xs'"
    :title="ariaText"
  >
    <span class="inline-flex items-center gap-1 text-gray-500">
      <span>{{ t('vecyRating.locationCaption') }}</span>
      <VecyLogo size="xs" />
    </span>
    <span class="font-semibold text-gray-900 tabular-nums">{{ scoreDisplay }}</span>
    <span v-if="showReviewCount && reviewCount > 0" class="text-gray-500 font-normal">
      ({{ reviewCount }})
    </span>
    <span v-if="zoneLabel" class="text-gray-500 truncate">· {{ zoneLabel }}</span>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import VecyLogo from './VecyLogo.vue';

const props = defineProps({
  score: {
    type: Number,
    required: true,
  },
  reviewCount: {
    type: Number,
    default: 0,
  },
  zoneLabel: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'sm',
  },
  showReviewCount: {
    type: Boolean,
    default: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  inline: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const triggerRef = ref(null);
const hoverVisible = ref(false);
const pinnedVisible = ref(false);
const tooltipStyle = ref({ top: '0px', left: '0px', transform: 'translateX(-50%)' });

const clampedScore = computed(() => Math.min(5, Math.max(0, props.score)));

const scoreDisplay = computed(() => `${Math.round(clampedScore.value)}/5`);

const tooltipText = computed(() => t('vecyRating.tooltip'));

const isTooltipVisible = computed(() => hoverVisible.value || pinnedVisible.value);

const ariaText = computed(() =>
  t('vecyRating.ariaLabel', {
    zone: props.zoneLabel || t('vecyRating.defaultZone'),
    score: scoreDisplay.value,
    count: props.reviewCount,
  }),
);

function updateTooltipPosition() {
  const el = triggerRef.value;
  if (!el) return;

  const rect = el.getBoundingClientRect();
  tooltipStyle.value = {
    top: `${rect.bottom + 8}px`,
    left: `${rect.left + rect.width / 2}px`,
    transform: 'translateX(-50%)',
  };
}

function showTooltip() {
  updateTooltipPosition();
  hoverVisible.value = true;
}

function hideTooltip() {
  hoverVisible.value = false;
}

function onMouseEnter() {
  showTooltip();
}

function onMouseLeave() {
  hideTooltip();
}

function onClick() {
  pinnedVisible.value = !pinnedVisible.value;
  if (pinnedVisible.value) {
    updateTooltipPosition();
  }
}

function onDocumentClick(event) {
  if (!pinnedVisible.value) return;
  const trigger = triggerRef.value;
  if (trigger && !trigger.contains(event.target)) {
    pinnedVisible.value = false;
  }
}

function onViewportChange() {
  if (isTooltipVisible.value) {
    updateTooltipPosition();
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  window.addEventListener('scroll', onViewportChange, true);
  window.addEventListener('resize', onViewportChange);
});

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick);
  window.removeEventListener('scroll', onViewportChange, true);
  window.removeEventListener('resize', onViewportChange);
});
</script>
